<script setup lang="ts">
import { isCategoryPageContent } from '~/types/api/page'

const route = useRoute()
const routePath = computed(() => route.path)
const asyncDataCacheKey = computed(() => `pages-${routePath.value}`)

const { data: pageContent, status, error } = await useLazyAsyncData(asyncDataCacheKey.value, async () => {
  const pageContent = await useWagtailApiPageGetByPath(route.path)
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  return pageContent
}, { watch: [routePath] })

if (error.value) {
  throw createError({ statusCode: 404, statusMessage: error.value.message })
}

useHead(usePageHead({ pageContent }))
</script>

<template>
  <div>
    <LazyAppPageLoading v-if="status === 'pending'" />
    <template v-else-if="isCategoryPageContent(pageContent)">
      <LazyAppBreadcrumb
        :crumbs="pageContent.meta?.breadcrumb_data || []"
        :current-title="pageContent.title"
      />

      <LazyAppHeaderPage
        :tags="pageContent.tags"
        :image="pageContent.featured_image_rendered"
        :image-caption="pageContent.featured_image_caption"
      >
        <template #title>
          {{ pageContent.title }}
        </template>

        <template
          v-if="pageContent.about_html"
          #subtitle
        >
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="prose prose-lg mx-auto"
            v-html="pageContent.about_html"
          />
          <!-- eslint-enable vue/no-v-html -->
        </template>
      </LazyAppHeaderPage>

      <template v-if="pageContent.body">
        <div
          v-for="(block, index) in pageContent.body"
          :key="block.id"
        >
          <AppPostBlock
            :id="block.id"
            :key="block.id"
            :type="block.type"
            :content="block.value"
            :reverse="index % 2 !== 0"
          />
        </div>
      </template>
    </template>
  </div>
</template>
